import {
  Box,
  darken,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Timer } from "./types";
import {
  PauseOutlined,
  PlayArrowOutlined,
} from "@mui/icons-material";

import { useAppSelector } from "../../store/hooks";
import { useDispatch } from "react-redux";

import useConfig from "../../core/useConfig";
import { useTimerContext } from "./TimerLoader";
import { timerActions } from "../../store/timerSlice";
import { dateStringBasedOnTimeZone, dateStringForFromAndToStringFormat } from "./helper";
import { GetAllTimersMutation } from "./mutations/GetAllTimersMutation";
import Spinner from "../../components/Spinner";
import { useBroadcast } from "../../utils/broadcast/useBroadcast";
import { useEmail } from "../../core/MailLoader";

// Using the mui inline class css.
const useStyle = makeStyles()(({ spacing, typography, palette }: Theme) => ({
  root: {
    padding: "0px",
    margin: "0px",
  },
  parantMainContainer: {
    position: "relative",
    display: "flex",
    width: "fit-content",
    height: "fit-content",
    // overflow: "hidden",
    gap: spacing(0.5),
    alignItems: "center",
    borderRadius: spacing(0.5),
    paddingLeft: "4px",
  },
  headerPosition: {
    // border: `1px solid ${palette?.primary.contrastText}`,
    // borderRadius: spacing(1),
    backgroundColor: darken(palette.primary.main, 0.2),
  },
  mainContainer: {
    width: "fit-content",
    height: "100%",
    display: "flex",
    alignItems: "center",
    // padding: spacing(0.5),
    backgroundColor: "transparent",
  },
  timerViewContainer: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  listContainer: {
    height: "100% !important",
  },
  divider: {
    height: "1.5em",
    marginRight: "-0.3em",
  },
  timerActionContainer: {
    display: "flex",
    alignItems: "center",
  },
  timerView: {
    fontFamily: typography.fontFamily,
    fontWeight: typography?.fontWeightLight,
    fontSize: "17px",
    color: palette?.primary.contrastText,
    minWidth: "75px",
    maxWidth: "85px",
    textAlign: "center",
  },
  listContainerDrawer: {
    position: "absolute",
    height: "400px",
    width: "500px",
    border: "1px solid red",
    left: "50%",
    transform: "translate(-50%, 0%)",
    backgroundColor: palette.background.paper,
    borderRadius: spacing(1),
    zIndex: 1,
    opacity: 0,
    transition: "4s all ease-in-out",
  },
  listContainerDrawerFade: {
    opacity: 1,
  },
  overlay: {
    position: "fixed",
    top: "0px",
    left: "0px",
    height: "100vh",
    width: "100vw",
    zIndex: 0,
  },
  listHeaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

interface TimerProps extends Timer {
  isPlayButton?: boolean;
  isClickAble?: boolean;
  isChild?: boolean;
  position?: "header" | "body";
  uniqueKey?: string | undefined;
  drawerRef?: any;
  isCurrentDateTimer?: boolean | undefined;
}

/**
 * TimerManager Component
 *
 * This component serves as a Timer manager that allows users to:
 * - Start, pause, and track timers.
 * - Display a timer in different positions (e.g., "header" or "body").
 * - Manage timer interactions for child and global timers.
 * - Open a list drawer to view or manage multiple timers.
 *
 * Props:
 * - `isChild` (boolean): Indicates if the timer is a child timer.
 * - `durationInSeconds` (number): Initial duration of the timer in seconds.
 * - `handlePlay` (function): Function to handle timer play.
 * - `id` (string): Unique identifier for the timer.
 * - `position` ("header" | "body"): Determines the position where the timer is rendered.
 * - `isTimerRunning` (boolean): Indicates if the timer is currently running.
 * - `isPlayButton` (boolean): Determines if the play button is displayed.
 * - `isClickAble` (boolean): Determines if the timer is clickable to start or pause.
 * - `uniqueKey` (string): Unique key for identifying the timer instance.
 */
const TimerManager: React.FC<TimerProps> = ({
  isChild,
  durationInSeconds = 0,
  position = "header",
  isTimerRunning = false,
  isPlayButton = true,
  isClickAble = true,
  uniqueKey,
  drawerRef,
  isCurrentDateTimer,
}) => {
  const dispatch = useDispatch();
  const { classes } = useStyle();
  const theme = useTheme();
  const elapsedRef = useRef<number | null>(durationInSeconds); // Initialize with the provided durationInSeconds
  const displayRef = useRef<null | HTMLHeadingElement>(null); // To update the timer display
  const [isDrawer, setIsDrawer] = useState<boolean>(false);
  const { data: timers } = useAppSelector((state) => state.timer.timers);
  const storeTimer = useAppSelector((state) => state.timer.timer);

  const [lcLoading, setLcLoading] = useState<boolean>(false);
  const [localElapsedStateValue, setLocalElapsedStateValue] = useState<number>(0);
  const [lcIsRunning, setLcIsRunning] = useState<boolean>(false);
  const { timezone, isSubscriptionActive, companies, activeLocations } = useConfig();
  const [fetchTimersLoading, setFetchTimersLoading] = useState<boolean>(false);
  const {mailId} = useEmail()
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { channel, sendMessage } = useBroadcast();
  const {
    loading,
    handleResetPausedTimer,
    localValues,
    elapsed,
    isGlobalTimerRunning,
    startTimer,
    pauseTimer,
    elapsedRef: contextElapsed,
  } = useTimerContext();

  // Function to format time as HH:MM:SS
  const formatTime = (seconds: number = 0) => {
    if (Number.isNaN(seconds)) {
      return `00:00:00`;
    }
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0");
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60)?.toString()?.padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  // Function to close list drawer
  const handleCloseListDrawer = () => {
    setIsDrawer(false);
    dispatch(
      timerActions.getData({
        rowCount: 0,
        hasMore: false,
        data: [],
      })
    );
  };

  // Set the initial timer display based on the durationInSeconds
  useEffect(() => {
    if (displayRef.current) {
      displayRef.current.textContent = formatTime(elapsedRef.current as number); // Set initial value
      elapsedRef.current = durationInSeconds;
      setLocalElapsedStateValue(durationInSeconds as number);
    }
  }, [durationInSeconds]); // Depend on durationInSeconds to update correctly on prop changes

  // CASE FOR NAVABAR TIMER
  useEffect(() => {
    if (isGlobalTimerRunning === true && isChild === false && lcIsRunning === false) {
      setLcIsRunning(true);
    }
    if (isGlobalTimerRunning === false && isChild === false && lcIsRunning) {
      setLcIsRunning(false);
    }
  }, [isChild, isGlobalTimerRunning]);

  //  CASE FOR CHILD OR BODY TIMERS
  useEffect(() => {
    if (isTimerRunning === true && isChild === true) {
      setLcIsRunning(true);
    }
    if (isTimerRunning === false && isChild === true) {
      setLcIsRunning(false);
    }
  }, [isChild, isTimerRunning]);

  useEffect(() => {
    if (isDrawer) {
      setFetchTimersLoading(true);
      const currentDateBasedOnTimeZone = dateStringBasedOnTimeZone(timezone!);
      const modifiedDates = dateStringForFromAndToStringFormat(
        currentDateBasedOnTimeZone!,
        timezone!
      );
      const payload = {
        // fromDate: localValues?.fromDate
        //   ? moment.tz(localValues?.fromDate, timezone).startOf("day").toISOString()
        //   : modifiedDates?.fromDate,
        // // fromDate: "2024-12-24T00:00:01.000Z",
        // toDate: localValues?.toDate
        //   ? moment.tz(localValues?.toDate, timezone).endOf("day").toISOString()
        //   : modifiedDates?.toDate,
        resourceId:mailId
      };

      dispatch(
        GetAllTimersMutation<Timer>(payload, {
          onLoading(lcLoading) {
            setFetchTimersLoading(lcLoading);
          },
        })
      );
    }
    handleResetPausedTimer && handleResetPausedTimer();

    if (position !== "body" && !isChild && !drawerRef.current) {
      drawerRef.current = setIsDrawer;
    } // FOR CLOSE THE DRAWER
  }, [localValues?.toDate, mailId]);

  const handleLocalStartTimer = () => {
    setLcLoading(true);

    startTimer({
      id: isChild ? uniqueKey : storeTimer?.id,
      durationInSeconds: isChild ? (isCurrentDateTimer ? elapsedRef.current : 0) : 0,
      isRunning: isChild ? isTimerRunning : isGlobalTimerRunning,
      callbacks: {
        loadingHandler: setLcLoading,
        isRunningTimerHandler: isCurrentDateTimer ? setLcIsRunning : null,
      },
    });
  };

  const handleLocalPauseTimer = () => {
    if (isChild) {
      elapsedRef.current = elapsed as number;
      setLocalElapsedStateValue(elapsed!);
    } else {
      elapsedRef.current = 0;
      setLocalElapsedStateValue(0);
      if (displayRef.current) {
        displayRef.current.textContent = formatTime(0);
      }
    }
    setLcLoading(true);
    pauseTimer({
      id: isChild ? uniqueKey : storeTimer?.id,
      durationInSeconds: elapsed,
      isRunning: isChild ? isTimerRunning : isGlobalTimerRunning,
      callbacks: { loadingHandler: setLcLoading, isRunningTimerHandler: setLcIsRunning },
      isMatter: storeTimer?.matterId ? true : false,
    });

    // dispatch(timerActions.getRecord({}));
  };

  useEffect(() => {
    try {
      channel.onmessage = (ev) => {
        if (ev.data?.type === "messageSending") {
          if (buttonRef.current) {
            buttonRef.current.click();
          }
        }
      };
    } catch (err) {
      console.warn("BroadcastChannel unsupported; you may need to use storage polling", err);
      // Optionally fallback to storage polling here.
    }

    return () => {
      if (channel) {
        channel.close();
      }
    };
  }, []);
  //
  // Note to get more UI space on MobileView when more than one company and location are there.

  return (
    // <Button onClick={()=>handleLocalStartTimer()}>Start Timer</Button>
    
    <Box
      className={`${classes?.parantMainContainer} ${
        position === "header" ? classes.headerPosition : ""
      }`}
    >
      {
        <Box
          sx={{ cursor: loading ? "not-allowed" : "pointer" }}
          className={classes?.mainContainer}
        >
          {isPlayButton && (
            <Box className={classes.timerActionContainer}>
              {
                // lcLoading ? <IconButton ><Spinner /></IconButton> : !lcIsRunning ? <IconButton disabled={(!loading && isSubscriptionActive) ? false : true} onClick={() => {
                lcLoading ? (
                  <IconButton sx={{ padding: "0px" }}>
                    <Spinner />
                  </IconButton>
                ) : (!lcIsRunning && isChild && storeTimer?.id !== uniqueKey && !isTimerRunning) ||
                  (!lcIsRunning && !isChild && !isGlobalTimerRunning) ? (
                  <IconButton
                    sx={{ padding: "0px" }}
                    disabled={!loading && isSubscriptionActive ? false : true}
                    onClick={() => {
                      handleLocalStartTimer();
                    }}
                  >
                    <PlayArrowOutlined
                      fontSize="medium"
                      style={{
                        paddingBottom: "1px",
                        color:
                          position === "body"
                            ? theme?.palette?.text?.primary
                            : theme.palette?.primary.contrastText,
                      }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    sx={{ padding: "0px" }}
                    disabled={!loading && isSubscriptionActive ? false : true}
                    onClick={() => {
                      handleLocalPauseTimer();
                    }}
                    ref={buttonRef}
                  >
                    <PauseOutlined
                      fontSize="medium"
                      style={{
                        paddingBottom: "1px",
                        color:
                          position === "body"
                            ? theme?.palette?.text?.primary
                            : theme.palette?.primary.contrastText,
                      }}
                    />
                  </IconButton>
                )
              }
            </Box>
          )}
          <Box
            className={classes.timerViewContainer}
            onClick={() => {
              if (!isGlobalTimerRunning) {
                isClickAble && loading?.startLoading === false && isSubscriptionActive && handleLocalStartTimer();
              } else {
                isClickAble && loading?.pauseLoading === false && isSubscriptionActive && handleLocalPauseTimer();
              }
            }}
          >
            <Typography
              component={"h2"}
              style={{
                color: position === "body" ? theme?.palette?.text?.primary : "",
              }}
              className={classes.timerView}
              ref={displayRef}
            >
              {lcLoading
                ? `00:00:00`
                : formatTime(
                    (isGlobalTimerRunning && !isChild) || (isChild && isTimerRunning)
                      ? (elapsed as number)
                      : (localElapsedStateValue as number)
                  )}
            </Typography>
          </Box>
        </Box>
      }
    </Box>
  );
};

export default React.memo(TimerManager);
